import React from 'react';
import { Link } from 'react-router-dom';
import {formatNumber} from '@/utils/format';
import {Power20Company} from "@/types/company";
import LineChart from './LineChart';

interface Power20CompanyResponse {
  tableData: Power20Company[];
}

function formatPriceChange(value:any, options:number){
  if(value>0){
    return `+${+Number(value*100).toFixed(options)}%`
  } else {
    return `${+Number(value*100).toFixed(options)}%`
  }
}

// Helper function to transform priceSeries data for LineChart
function transformPriceSeriesData(priceSeries: Record<string, number>) {
  return Object.entries(priceSeries).map(([date, value]) => ({
    date,
    value,
    category: 'Price'
  }));
}

export const Power20Table: React.FC<Power20CompanyResponse> = ({ tableData }) => {
  return (
      <div className="overflow-x-auto rounded-t-md sm:rounded-t-lg md:rounded-t-xl">
        <table className="min-w-full">
          <thead>
          <tr className="text-xs sm:text-sm md:text-base lg:text-lg pt-3 pb-3 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-white">
            <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-center border border-gray-800 rounded-tl-md sm:rounded-tl-lg md:rounded-tl-xl">#</th>
            <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-left border border-gray-800">Company</th>
            <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-center border border-gray-800">Valuation</th>
            <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-center border border-gray-800">Price</th>
            <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 bg-[#444648] text-center border border-gray-800 rounded-tr-md sm:rounded-tr-lg md:rounded-tr-xl"></th>
          </tr>
          </thead>
          <tbody>
          {tableData.map((item, index) => (
              <tr key={index} className="hover:bg-[#333434]">
                <td className="px-2 sm:px-4 md:px-6 py-1 sm:py-2 whitespace-nowrap border border-gray-800 text-center text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]">
                  {index + 1}
                </td>
                <td className="px-2 sm:px-4 md:px-6 py-1 sm:py-2 whitespace-nowrap border border-gray-800">
                  <div className="flex items-center">
                    <div className="avatar">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 lg:w-10 lg:h-10 rounded-full">
                        <img
                            className="rounded-full object-cover w-full h-full"
                            src={item.logoLink}
                            alt={item.name}
                        />
                      </div>
                    </div>
                    <div className="ml-2 sm:ml-2 md:ml-3">
                      <div className="font-medium text-white text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]">
                        <Link to={`/company-detail/${item.id}?name=${item.name}`} className="hover:text-blue-400">
                          {item.name}
                        </Link>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-2 sm:px-4 md:px-6 py-1 sm:py-2 whitespace-nowrap border border-gray-800">
                  <div className="text-white text-center text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]">
                    <div>{formatNumber(item.valuation, { prefix: '$', compact: true, decimals: 2 })}</div>
                  </div>
                </td>
                <td className="px-2 sm:px-4 md:px-6 py-1 sm:py-2 whitespace-nowrap border border-gray-800">
                  <div className="text-white text-center text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px] sm:flex align-center justify-center">
                    <div>{formatNumber(item.price, { prefix: '$', compact: true, decimals: 2 })}</div>
                    <div className="flex items-center">
                      <div className="text-xs text-gray-400 ml-1 sm:ml-2 md:ml-3 mt-0.5 sm:mt-1">
                        {item.priceDeltaPercentage>0?(
                            <svg className="icon flag-svg w-3 sm:w-4 md:w-5 lg:w-5 h-3 sm:h-4 md:h-5 lg:h-5 mr-1" viewBox="0 0 1024 1024" version="1.1"
                                 xmlns="http://www.w3.org/2000/svg" p-id="3820" width="200" height="200">
                              <path d="M0 767.909l512.029-511.913L1024 767.909 0 767.909z" fill="#00c853"
                                    p-id="3821"></path>
                            </svg>

                        ):(
                            <svg className="icon flag-svg w-3 sm:w-4 md:w-5 lg:w-5 h-3 sm:h-4 md:h-5 lg:h-5 mr-1" viewBox="0 0 1024 1024" version="1.1"
                                 xmlns="http://www.w3.org/2000/svg" p-id="3633" id="mx_n_1741777127914" width="200"
                                 height="200">
                              <path d="M1024 255.996 511.971 767.909 0 255.996 1024 255.996z" fill="#d50000"
                                    p-id="3634"></path>
                            </svg>
                        )}
                      </div>
                      <div className={item.priceDeltaPercentage>0?'text-green-500':'text-red-500'}>{formatPriceChange(item.priceDeltaPercentage,2)}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 sm:px-4 py-1 sm:py-2 md:py-4 whitespace-nowrap border border-gray-800 flex justify-center items-center">
                  <div className="w-24 h-9 sm:h-10 md:h-11 lg:h-12">
                    <LineChart
                      className="w-full h-full justify-center items-center hidden xs:hidden sm:hidden md:hidden lg:flex"
                      data={transformPriceSeriesData(item.priceSeries)}
                      width={124}
                      height={48}
                      chartColor={item.priceDeltaPercentage > 0 ? '#00c853' : '#d50000'}
                    />

                    <LineChart
                      className="w-full h-full justify-center items-center hidden xs:hidden sm:hidden md:flex lg:hidden"
                      data={transformPriceSeriesData(item.priceSeries)}
                      width={116}
                      height={44}
                      chartColor={item.priceDeltaPercentage > 0 ? '#00c853' : '#d50000'}
                    />

                    <LineChart
                      className="w-full h-full justify-center items-center hidden xs:hidden sm:flex md:hidden lg:hidden"
                      data={transformPriceSeriesData(item.priceSeries)}
                      width={108}
                      height={40}
                      chartColor={item.priceDeltaPercentage > 0 ? '#00c853' : '#d50000'}
                    />

                    <LineChart
                      className="w-full h-full justify-center items-center hidden xs:flex sm:hidden md:hidden lg:hidden"
                      data={transformPriceSeriesData(item.priceSeries)}
                      width={100}
                      height={36}
                      chartColor={item.priceDeltaPercentage > 0 ? '#00c853' : '#d50000'}
                    />

                    <LineChart
                      className="w-full h-full justify-center items-center flex xs:hidden sm:hidden md:hidden lg:hidden"
                      data={transformPriceSeriesData(item.priceSeries)}
                      width={92}
                      height={32}
                      chartColor={item.priceDeltaPercentage > 0 ? '#00c853' : '#d50000'}
                    />

                  </div>
                </td>
              </tr>
          ))}
          </tbody>
        </table>
      </div>
  );
};
